import { translateBackend } from '@assets/i18n/config';
import ViewAllProfilesIcon from '@assets/images/icons-filled/view-all-profiles.svg';
import { FeatureFlagTypes } from '@generated/graphql';
import { AuthenticationConnections } from '@interfaces/organization';
import {
  OrganizationUserAccessTypes,
  OrganizationUserEntityRelationTypes,
} from '@interfaces/organizationUser';
import { NavigationStyles } from '@interfaces/role';

import { InlineImage } from '@ovComponents/2-component/inlineImage/inlineImage';

import { Menu } from '@ovComponents/2-component/menu/menu';
import { MenuItem } from '@ovComponents/2-component/menuItem/menuItem';
import { ViewAllProfilesDialog } from '@ovComponents/3-pattern/viewAllProfilesDialog/viewAllProfilesDialog';
import type { IUserEntity } from '@ovComponents/3-pattern/viewAllProfilesDialog/viewAllProfilesDialog.types';
import { CreateEntityWorkflow } from '@ovComponents/4-module/createEntityWorkflow/createEntityWorkflow';
import { MemberTag } from '@pages/household/components/memberTag';
import { AnalyticsContext } from '@providers/analyticsProvider';
import {
  type ActiveEntityProps,
  UserContext,
  useOrganizationFeatureFlag,
  usePermission,
} from '@providers/userContextProvider';
import { ovAnalyticsEvents } from '@utils/analytics/analytics';
import { entityInitials, entityName } from '@utils/common';
import filterHouseholdsWithAccess from '@utils/filterHouseholdsWithAccess';
import { safeWindowOpen } from '@utils/urlSanitization';
import { usePageState, useSearchParams } from '@utils/usePageState';
import { Color } from 'hephaestus/core';
import {
  Avatar,
  Box,
  Divider,
  IconButton,
  Typography,
  useSnackbar,
  useTheme,
} from 'hephaestus/web';
import {
  AddCircleOutlined as AddCircleOutlineIcon,
  CheckRounded as CheckRoundedIcon,
  Language as LanguageIcon,
  Launch as LaunchIcon,
  Logout as LogoutIcon,
  MenuBook as MenuBookIcon,
  NotificationsNone as NotificationsNoneIcon,
  Person,
  AccountCircle as PersonIcon,
  SupportAgent as SupportAgentIcon,
} from 'hephaestus/webIcons';
import React, {
  type FC,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChangeAvatarModal } from '../changeAvatarModal/changeAvatarModal';
import { LanguageSelectionModal } from '../languageSelectionModal/languageSelectionModal';
import { LoginSettingModal } from '../loginSettingModal/loginSettingModal';
import { NotificationPreferencesModal } from '../notificationPreferencesModal/notificationPreferencesModal';
import { useEntityMenuTokens } from './entityMenu.tokens';
import { useEntityMenuColors } from './entityMenuColors';

const MAX_PROFILES_IN_MENU = 4;

type IEntityMenuProps = {
  logout: () => void;
  showOnlyLogout?: boolean;
  /** Foreground colour of the surface this sits on; tints the hover states. */
  color?: string;
};

const EntityMenu: FC<IEntityMenuProps> = ({
  logout,
  showOnlyLogout,
  color,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { t } = useTranslation(['entityMenu', 'navMenu', 'shared']);
  const { enqueueSnackbar } = useSnackbar();
  const { sendAnalytic } = useContext(AnalyticsContext);
  const canWriteProfile = usePermission('write:organization_user_profile');
  const allowAddingNewProfile = useOrganizationFeatureFlag(
    FeatureFlagTypes.AllowAddingANewProfile,
  );
  const theme = useTheme();
  const tokens = useEntityMenuTokens(theme);
  const hoverTint = color ?? theme.color.surface;
  const {
    userContext,
    activeEntity,
    activeHousehold,
    activeOrganization,
    setActiveEntity,
    setActiveHousehold,
  } = useContext(UserContext);

  const [createEntityOpen, setCreateEntityOpen] = usePageState(
    false,
    'createEntity',
  );
  const [updateLanguageOpen, setUpdateLanguageOpen] = useState(false);
  const [
    updateNotificationPreferencesOpen,
    setUpdateNotificationPreferencesOpen,
  ] = useState(false);
  const [updateAvatarOpen, setUpdateAvatarOpen] = useState(false);
  const [viewAllProfiles, setViewAllProfiles] = useState(false);
  const [autoOpenWorkflow, setAutoOpenWorkflow] = useState(true);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [loginSettingsOpen, setLoginSettingsOpen] = useState(false);

  const open = Boolean(anchorEl);
  const entities: IUserEntity[] =
    userContext.accessType === OrganizationUserAccessTypes.REP_CODE
      ? (userContext?.repCodeEntities ?? [])
      : (userContext?.entities ?? []);
  const nonAffiliateEntities = entities.filter((x) => !x.entity.affiliateOnly);
  const autoOpenInParams = params.get('autoOpenWorkflow');

  let allowLogout = activeOrganization.allowLogout ?? true;
  if (
    userContext.organization &&
    activeOrganization.id !== userContext.organization.id
  ) {
    allowLogout = true;
  }

  const handleClose = () => setAnchorEl(null);
  const handleClick = (event: any) => {
    sendAnalytic(ovAnalyticsEvents.homepageProfileSelect);
    setAnchorEl(event.currentTarget);
  };

  useEffect(() => {
    if (
      !activeEntity ||
      !activeEntity.id ||
      !(userContext.role?.navigationStyle === NavigationStyles.SIMPLE)
    )
      return;

    let path = '';
    const households = filterHouseholdsWithAccess(
      activeEntity?.households || [],
      activeEntity?.id,
      userContext,
    );
    const isHouseholdDefaultView =
      households.length > 0 && userContext.role?.makeHouseholdDefaultView;

    if (
      isHouseholdDefaultView &&
      activeHousehold?.id &&
      (!location.pathname.includes('households') ||
        !location.pathname.includes(activeHousehold?.id))
    ) {
      path = `/households/${activeHousehold?.id}`;
    } else if (
      !isHouseholdDefaultView &&
      !location.pathname.includes(activeEntity.id || '')
    ) {
      path = `/clients/${activeEntity?.id}`;
    }

    if (path) {
      if (autoOpenWorkflow || autoOpenInParams) {
        // Sets the autoOpenWorkflow only when initially loading the page for the first entity
        navigate(`${path}?autoOpenWorkflow=true`);
        setAutoOpenWorkflow(false);
      } else {
        navigate(path);
      }
    }
  }, [activeEntity, navigate, activeHousehold, userContext]);

  const isUnifiedClientExperience =
    userContext.role?.navigationStyle === NavigationStyles.SIMPLE;

  const profileName = useCallback(
    (entity?: ActiveEntityProps) => entityName(entity) || entity?.primaryEmail,
    [],
  );
  const profileInitials = useCallback(
    (entity?: ActiveEntityProps) => entityInitials(entity),
    [],
  );

  const getGroupedEntitiesSortedByType = (type?: string) => {
    const isIndividual = type?.toUpperCase() === 'INDIVIDUAL';

    const filtered = nonAffiliateEntities.filter((item) =>
      isIndividual
        ? item.entity.type === 'INDIVIDUAL'
        : item.entity.type !== 'INDIVIDUAL',
    );

    const sorted = [...filtered].sort((a, b) =>
      profileName(a.entity).localeCompare(profileName(b.entity)),
    );

    const active = sorted.filter((item) => item.entity.id === activeEntity?.id);
    const nonActive = sorted.filter(
      (item) => item.entity.id !== activeEntity?.id,
    );

    return [...active, ...nonActive];
  };

  const handleEntitySelect = (item: IUserEntity) => {
    handleClose();
    const households = filterHouseholdsWithAccess(
      item.entity?.households || [],
      item.entity?.id,
      userContext,
    );
    const isHouseholdDefaultView =
      !!userContext.role?.makeHouseholdDefaultView && households.length > 0;
    if (isHouseholdDefaultView) {
      const household = households[0];
      setActiveHousehold(household);
      navigate(`/households/${household?.id}/clients/${item.entity.id}`);
    }
    setActiveEntity(item.entity);
  };

  const handleUrlItem = (analyticEvent: string, url: string) => {
    sendAnalytic(analyticEvent);
    handleClose();
    safeWindowOpen(url, {
      target: '_blank',
      onError: (errorMsg) => {
        enqueueSnackbar(errorMsg, { variant: 'error' });
      },
    });
  };

  const individualEntities = getGroupedEntitiesSortedByType('individual');
  const nonIndividualEntities = getGroupedEntitiesSortedByType();

  const activeEntityItem = nonAffiliateEntities.find(
    (en) => en.entity.id === activeEntity?.id,
  ) as IUserEntity;
  const totalEntitiesCount =
    individualEntities.length + nonIndividualEntities.length;

  return (
    <>
      <IconButton
        aria-label='more actions'
        onClick={handleClick}
        data-testid='profile-menu-icon'
        size='small'
        sx={{
          '&:hover': {
            backgroundColor: Color(hoverTint).alpha(0.08).string(),
          },
          '&:active': {
            backgroundColor: Color(hoverTint).alpha(0.16).string(),
          },
          '&:focus-visible': {
            backgroundColor: Color(hoverTint).alpha(0.16).string(),
          },
          mr: '-6px',
        }}
      >
        <span>
          <Avatar
            size='xsmall'
            sx={{
              color: tokens.avatar.foregroundColor,
              bgcolor: tokens.avatar.backgroundColor,
            }}
          >
            {userContext.avatar ? (
              <InlineImage
                fileDocumentId={userContext.avatar}
                style={{ objectFit: 'cover', height: '100%' }}
              />
            ) : (
              (activeEntity && isUnifiedClientExperience
                ? (profileInitials(activeEntity) ?? '')
                : `${userContext.firstName?.[0] ?? ''}${userContext.lastName?.[0] ?? ''}`
              )?.toUpperCase()
            )}
          </Avatar>
        </span>
      </IconButton>
      <Menu
        id='profile-menu'
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        disableScrollLock={true}
        sx={{
          '.MuiPaper-root': {
            boxShadow: '0 0 8px rgba(0,0,0,0.1)',
            width: '230px',
            borderRadius: tokens.borderRadius,
          },
        }}
      >
        {!showOnlyLogout &&
          isUnifiedClientExperience && [
            ...(totalEntitiesCount <= MAX_PROFILES_IN_MENU
              ? [
                  ...individualEntities.map((en, index) => (
                    <EntityMenuItem
                      item={en}
                      isActive={en.entity.id === activeEntity?.id}
                      index={index}
                      onClick={() => handleEntitySelect(en)}
                    />
                  )),
                  ...(individualEntities.length > 0 &&
                  nonIndividualEntities.length > 0
                    ? [<Divider />]
                    : []),
                  ...(nonIndividualEntities.length > 0
                    ? [
                        <Box sx={{ px: 2, py: 1 }}>
                          <Typography
                            variant='labelSmall'
                            sx={{ fontSize: '10px' }}
                            color='textSecondary'
                          >
                            {t(
                              'navMenu:menu.nonIndividualClients',
                            ).toUpperCase()}
                          </Typography>
                        </Box>,
                        ...nonIndividualEntities.map((en, index) => (
                          <EntityMenuItem
                            item={en}
                            isActive={en.entity.id === activeEntity?.id}
                            index={index}
                            onClick={() => handleEntitySelect(en)}
                          />
                        )),
                      ]
                    : []),
                ]
              : []),

            ...(totalEntitiesCount > MAX_PROFILES_IN_MENU
              ? [
                  <EntityMenuItem
                    item={activeEntityItem}
                    isActive
                    index={0}
                    onClick={() => handleEntitySelect(activeEntityItem)}
                  />,

                  <MenuItemWithImage
                    image={ViewAllProfilesIcon}
                    title={t('viewAllProfiles')}
                    testId='view-all-profile'
                    onClick={() => {
                      handleClose();
                      setViewAllProfiles(true);
                    }}
                  />,
                ]
              : []),
            <Divider />,
            ...(allowAddingNewProfile
              ? [
                  <MenuItemWithIcon
                    MuiSvgIcon={AddCircleOutlineIcon}
                    title={t('addNewEntity')}
                    testId='add-new-entity'
                    onClick={() => {
                      handleClose();
                      setCreateEntityOpen(true);
                    }}
                  />,
                ]
              : []),
          ]}
        {canWriteProfile && (
          <MenuItemWithIcon
            MuiSvgIcon={LanguageIcon}
            title={t('language')}
            testId='menuitem-language'
            onClick={() => {
              handleClose();
              setUpdateLanguageOpen(true);
            }}
          />
        )}
        {activeOrganization.enableLoginSettings &&
          (activeOrganization.defaultAuthenticationConnection !==
            AuthenticationConnections.EMAIL_OTP ||
            activeOrganization.loginUrl) && (
            <MenuItemWithIcon
              MuiSvgIcon={Person}
              title={translateBackend(activeOrganization.loginSettingsTitle)}
              testId='menuitem-loginSettings'
              onClick={() => {
                handleClose();

                const { defaultCustodianConnection } = activeOrganization;
                if (
                  defaultCustodianConnection &&
                  'enableCustodianCredentialsManager' in
                    defaultCustodianConnection &&
                  defaultCustodianConnection.enableCustodianCredentialsManager ===
                    true
                ) {
                  const { metadata } = defaultCustodianConnection;
                  const credsAppUrl = metadata?.find(
                    (m) => m.key === 'credentialsManagerUrl',
                  )?.value;
                  if (
                    typeof credsAppUrl === 'string' &&
                    credsAppUrl.trim() !== ''
                  ) {
                    const { protocol, host, pathname } = window.location;
                    const traceId = crypto
                      .randomUUID()
                      .substring(0, 12)
                      .replace(/-/g, '');
                    window.localStorage.setItem(
                      'custodianCredentialsManagerTraceId',
                      traceId,
                    );

                    const inboundRedirectUrl = `${protocol}//${host}${pathname}?custodianmeta=traceId:${traceId}`;
                    const toBeUrlEncoded = `redirect=${inboundRedirectUrl}`;
                    const outboundRedirectUrl = `${credsAppUrl}?${encodeURIComponent(toBeUrlEncoded)}`;
                    safeWindowOpen(outboundRedirectUrl, {
                      target: '_self',
                      onError: (errorMsg) => {
                        enqueueSnackbar(errorMsg, { variant: 'error' });
                      },
                    });
                    return;
                  }
                }

                if (activeOrganization.loginUrl) {
                  safeWindowOpen(activeOrganization.loginUrl, {
                    target: '_self',
                    onError: (errorMsg) => {
                      enqueueSnackbar(errorMsg, { variant: 'error' });
                    },
                  });
                } else {
                  setLoginSettingsOpen(true);
                }
              }}
            />
          )}
        {canWriteProfile && (
          <MenuItemWithIcon
            MuiSvgIcon={NotificationsNoneIcon}
            title={t('notifications')}
            testId='menuitem-notification-preferences'
            onClick={() => {
              handleClose();
              setUpdateNotificationPreferencesOpen(true);
            }}
          />
        )}
        {canWriteProfile && (
          <MenuItemWithIcon
            MuiSvgIcon={PersonIcon}
            title={t('updateAvatar')}
            testId='menuitem-updateAvatar'
            onClick={() => {
              handleClose();
              setUpdateAvatarOpen(true);
            }}
          />
        )}
        {activeOrganization.enableResourcesAndArticles && (
          <MenuItemWithIcon
            MuiSvgIcon={MenuBookIcon}
            SuffixMuiSvgIcon={LaunchIcon}
            title={t('resourcesArticles')}
            testId='menuitem-resourcesArticles'
            onClick={() => {
              const helpCentreUrl =
                translateBackend(activeOrganization?.translatedHelpCentreUrl) ||
                'https://onevest.zendesk.com/hc/en-us';
              handleUrlItem(
                ovAnalyticsEvents.homepageResourceArticlesSelect,
                helpCentreUrl,
              );
            }}
          />
        )}
        {activeOrganization.enableContactSupport && (
          <MenuItemWithIcon
            MuiSvgIcon={SupportAgentIcon}
            SuffixMuiSvgIcon={LaunchIcon}
            title={t('contactSupport')}
            testId='menuitem-contactSupport'
            onClick={() => {
              const supportUrl =
                translateBackend(activeOrganization?.translatedSupportUrl) ||
                'https://onevest.zendesk.com/hc/en-us/requests/new';
              handleUrlItem(
                ovAnalyticsEvents.homepageContactSupportSelect,
                supportUrl,
              );
            }}
          />
        )}
        {allowLogout && (
          <MenuItemWithIcon
            MuiSvgIcon={LogoutIcon}
            title={t('logout')}
            testId='menuitem-logout'
            onClick={() => {
              handleClose();
              logout();
            }}
          />
        )}
      </Menu>

      <LoginSettingModal
        open={loginSettingsOpen}
        setOpen={setLoginSettingsOpen}
      />

      <LanguageSelectionModal
        open={updateLanguageOpen}
        setOpen={setUpdateLanguageOpen}
      />

      <ChangeAvatarModal
        open={updateAvatarOpen}
        setOpen={setUpdateAvatarOpen}
      />

      <NotificationPreferencesModal
        open={updateNotificationPreferencesOpen}
        setOpen={setUpdateNotificationPreferencesOpen}
      />

      <CreateEntityWorkflow
        open={createEntityOpen}
        setOpen={setCreateEntityOpen}
      />

      <ViewAllProfilesDialog
        open={viewAllProfiles}
        onViewDetail={() => setViewAllProfiles(false)}
        onCancel={() => setViewAllProfiles(false)}
        onAddNewProfile={() => {
          setViewAllProfiles(false);
          setCreateEntityOpen(true);
        }}
      />
    </>
  );
};

export default EntityMenu;

const MenuItemWithImage = ({
  title,
  image,
  testId,
  onClick,
}: {
  title: string;
  image: string;
  testId: string;
  onClick: () => void;
}) => (
  <MenuItem key={title} data-testid={testId} onClick={onClick}>
    <Box
      sx={{
        marginRight: 1,
        display: 'flex',
        height: '32px',
        width: '32px',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <img src={image} alt='view-profile-icon' height='24px' width='24px' />
    </Box>
    <Typography variant='bodyMedium' sx={{ fontWeight: '500' }}>
      {title}
    </Typography>
  </MenuItem>
);

const MenuItemWithIcon = ({
  title,
  MuiSvgIcon,
  SuffixMuiSvgIcon,
  testId,
  onClick,
}: {
  title: string;
  MuiSvgIcon: React.ElementType;
  SuffixMuiSvgIcon?: React.ElementType;
  testId: string;
  onClick: () => void;
}) => (
  <MenuItem
    data-testid={testId}
    key={testId}
    onClick={onClick}
    sx={{
      pv: 2,
      display: SuffixMuiSvgIcon ? 'flex' : undefined,
      justifyContent: SuffixMuiSvgIcon ? 'space-between' : undefined,
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <MuiSvgIcon sx={{ mr: 1, fontSize: 18 }} />
      <Typography
        variant='bodyMedium'
        sx={{
          fontWeight: 500,
          maxWidth: '150px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {title}
      </Typography>
    </Box>
    {SuffixMuiSvgIcon && <SuffixMuiSvgIcon sx={{ fontSize: 18 }} />}
  </MenuItem>
);

const EntityMenuItem = ({
  item,
  isActive,
  index,
  onClick,
}: {
  item: IUserEntity;
  isActive: boolean;
  index: number;
  onClick: () => void;
}) => {
  const { userContext } = useContext(UserContext);
  const { colors, colorsInner } = useEntityMenuColors();
  const { t } = useTranslation(['entityMenu']);

  const profileName = useCallback(
    (e?: ActiveEntityProps) => entityName(e) || e?.primaryEmail,
    [],
  );
  const profileInitials = useCallback(
    (ActiveEntity?: ActiveEntityProps) => entityInitials(ActiveEntity),
    [],
  );

  const isAccessGranted =
    userContext.accessType === OrganizationUserAccessTypes.REP_CODE ||
    item?.relation !== OrganizationUserEntityRelationTypes.OWNER;
  const colorIndex = index % colors.length;

  return (
    <MenuItem
      key={item?.entity?.id}
      data-testid={`profile-${index}`}
      sx={{ pv: 2, display: 'flex', justifyContent: 'space-between' }}
      onClick={onClick}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            marginRight: 1,
            height: '32px',
            width: '32px',
          }}
        >
          <Avatar
            selected
            size='small'
            sx={{
              color: colors[colorIndex],
              bgcolor: colorsInner[colorIndex],
            }}
          >
            {profileInitials(item?.entity)}
          </Avatar>
        </Box>
        <Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography
              align='left'
              variant={isActive ? 'bodyLarge' : 'bodyMedium'}
              sx={{
                fontWeight: isActive ? 600 : 500,
                maxWidth: '140px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {profileName(item?.entity)}
            </Typography>
          </Box>
          {isAccessGranted && (
            <MemberTag
              sequenceNumber={colorIndex}
              name={t('entityMenu:accessGranted')}
            />
          )}
        </Box>
      </Box>
      {isActive && <CheckRoundedIcon style={{ fontSize: '16px' }} />}
    </MenuItem>
  );
};
